import {Injectable} from '@angular/core';
import {DatePipe} from '@angular/common';

import {Format} from '../enums/format.enum';

@Injectable()
export class DateService {

  // standard format and validation
  Format: typeof Format = Format;

  constructor (private datepipe: DatePipe) { }

  /**
   * @desc add UTC timezone when converting a string date to Date; otherwise depends on the timezone user is in, the returned Date may be different
                e.g. new Date("2018-01-01") may return "Mon Jan 01 2018 00:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 00:00:00 GMT-0500 (EST)" instead of
                "Mon Jan 01 2018 20:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 19:00:00 GMT-0500 (EST)"
   * @param date - date in string the format e.g "2018-01-01"
   * @returns: true - Date in local timezone
   */
  convertStringDateToUTCDate(date: string): Date {

    return new Date(date + "T00:00:00Z");
  }

  convertExcelSerialDateToUTCDateString(excelDateNumber: number) : string {

    return this.datepipe.transform(this.convertExcelSerialDateToUTCDate(excelDateNumber), Format.DataserviceDate);

  }

  convertExcelSerialDateToUTCDate(excelDateNumber: number) : Date {
    return new Date(Date.UTC(0, 0, excelDateNumber));
  }

  /**
   * @desc return min/max date used by primeng p-calendar which compares date value, not time value. e.g. date.getFullYear(), getMonth(), getDate()
   * @param date - date in string the format e.g "2018-01-01"
   */
  convertStringDateToPCalendarDate(date: string): Date {
    if (!date) {
      return null;
    }

    let utcDate = new Date(date + "T00:00:00Z");
    return new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
  }

  getDateBeforeGivenDate(date: string): Date {
    let utcDate = new Date(date + "T00:00:00Z");
    let givenDate: Date = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * 60000);
    return this.getDateBeforeGivenDateUsingDate(givenDate);
  }

  getDateBeforeGivenDateUsingDate(date: Date): Date {
    return new Date(date.getTime() - 24 * 60 * 60 * 1000);
  }

  /**
 * @desc add UTC timzone when converting a string year to Date; otherwise depends on the timezone user is in, the returned Date may be different
              e.g. new Date("2018-01-01") may return "Mon Jan 01 2018 00:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 00:00:00 GMT-0500 (EST)" instead of
              "Mon Jan 01 2018 20:00:00 GMT-0400 (EDT)" or "Mon Jan 01 2018 19:00:00 GMT-0500 (EST)"
 * @param date - year in string the format e.g "2018"
 * @returns: true - Date in local timezone
 */
  convertStringYearToUTCDate(year: string): Date {

    return new Date(year + "-01-01T00:00:00Z");
  }

  /**
   * @desc returns date with timezone offset added and dayOffset applied
   * @param date - date in either string ("2018-01-01") or local Date
   * @returns: true - if MM-dd-YYYY representation of two dates are equal, other wise returns false
   */
  dateWithOffset(date: string | Date, dayOffset?: number): Date {

    let value: Date = (date instanceof Date) ? date as Date : new Date(date);
    dayOffset = dayOffset || 0;

    // 86400000 mili-seconds in a day, 60000 milli-seconds in a minute
    // add dayOffset and timezone offset
    return new Date(value.getTime() + 86400000 * dayOffset + value.getTimezoneOffset() * 60000);
  }

  convertDateToStringDate(dateTime1: Date, format?: Format): string {
    if (format) {
      return this.datepipe.transform(dateTime1, format);
    } else {
      return this.datepipe.transform(dateTime1, Format.DataserviceDate);
    }
  }

  /**
   * @desc compares two input javascript dates and returns true if their date MM-dd-YYYY value in the spcified timezone are same.
   * @param dateTimie1, dateTime2
   * @returns: true - if MM-dd-YYYY representation of two dates are equal, other wise returns false
   */
  areDatesEqual(dateTime1: Date, dateTime2: Date, timeZone?: string): boolean {
    let date1, date2: string;

    // Angular 5 required to support TimeZone
    if (timeZone) {
      date1 = this.datepipe.transform(dateTime1, Format.TimesheetDate, timeZone);
      date2 = this.datepipe.transform(dateTime2, Format.TimesheetDate, timeZone);
      console.log(`comparing dates with timeZone: ${date1} =? ${date2}`);
    } else {
      date1 = this.datepipe.transform(dateTime1, Format.TimesheetDate);
      date2 = this.datepipe.transform(dateTime2, Format.TimesheetDate);
      console.log(`comparing dates: ${date1} =? ${date2}`);
    }

    return (date1 === date2);
  }

  isStringDateInRange(from: string, thru: string, date: string) {
    const fromDate = this.convertStringDateToUTCDate(from);
    const thruDate = this.convertStringDateToUTCDate(thru);
    const dateDate = this.convertStringDateToUTCDate(date);

    return (dateDate >= fromDate && dateDate <= thruDate);
  }

  rangeToString(fromDate: Date, thruDate?: Date): string {
    let range: string;
    if (fromDate) {
      range = `${fromDate.getFullYear()}.${fromDate.getMonth()}.${fromDate.getDate()}`;
      if (thruDate) {
        range += `-${thruDate.getFullYear()}.${thruDate.getMonth()}.${thruDate.getDate()}`;
      }
    }
    return range;
  }

  /**
   * @desc return range in string = from - thru
   * @param from in "YYYY/MM/DD"
   * @param thru in "YYYY/MM/DD"
   */
  toRange(from: string, thru?: string): string {
    return from + "-" + thru;
  }
}
